import { useState, useEffect } from "react";
import API from "../utils/api";

const EVENT_TYPES = ["auth", "work_order", "admin_action", "system"];

const EVENT_TYPE_COLORS = {
  auth: { bg: "#E3F4FB", color: "#0077A3" },
  work_order: { bg: "#E8F5E9", color: "#005700" },
  admin_action: { bg: "#FFF8E1", color: "#B38600" },
  system: { bg: "#F3E5F5", color: "#6A1B9A" },
};

// const SUBTYPE_LABELS = {
//   login:              '🔐 Login',
//   logout:             '🚪 Logout',
//   otp_request:        '📧 OTP Request',
//   otp_failed:         '❌ OTP Failed',
//   order_created:      '📋 Order Created',
//   order_assigned:     '👤 Order Assigned',
//   order_completed:    '✅ Order Completed',
//   order_not_completed:'❌ Not Completed',
//   order_cancelled:    '🚫 Cancelled',
//   order_reopened:     '🔄 Reopened',
//   order_deleted:      '🗑 Deleted',
//   order_restored:     '♻️ Restored',
//   order_edited:       '✏️ Edited',
//   order_dates_edited: '📅 Dates Edited',
//   staff_created:      '👤 Staff Created',
//   staff_updated:      '✏️ Staff Updated',
//   bulk_import:        '📥 Bulk Import',
//   delegation_created: '🤝 Delegation Created',
//   delegation_revoked: '🚫 Delegation Revoked',
// };

const SUBTYPE_LABELS = {
  login: "Login",
  logout: "Logout",
  otp_requested: "OTP Request",
  otp_failed: "OTP Failed",
  login_success: "Login",
  login_failed: "OTP Failed",
  login_expired: "OTP Expired",
  order_created: "Order Created",
  order_assigned: "Order Assigned",
  order_completed: "Order Completed",
  order_not_completed: "Not Completed",
  order_cancelled: "Cancelled",
  order_reopened: "Reopened",
  order_deleted: "Deleted",
  order_restored: "Restored",
  order_edited: "Edited",
  order_dates_edited: "Dates Edited",
  staff_created: "Staff Created",
  staff_updated: "Staff Updated",
  staff_deactivated: "Staff Deactivated",
  staff_activated: "Staff Activated",
  staff_bulk_import: "Bulk Import",
  customer_bulk_import: "Bulk Import",
  customer_created: "Customer Created",
  delegation_granted: "Delegation Created",
  delegation_revoked: "Delegation Revoked",
  admin_access_granted: "Admin Access Granted",
  admin_access_revoked: "Admin Access Revoked",
  region_created: "Region Created",
  region_updated: "Region Updated",
};

export default function SystemLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 50;

  // Filters
  const [eventType, setEventType] = useState("");
  const [eventSubtype, setEventSubtype] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchLogs = () => {
    setLoading(true);
    const params = {
      skip: page * limit,
      limit,
      ...(eventType && { event_type: eventType }),
      ...(eventSubtype && { event_subtype: eventSubtype }),
      ...(actorEmail && { actor_email: actorEmail }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    };
    API.get("/admin/logs", { params })
      .then((res) => {
        setLogs(res.data.logs);
        setTotal(res.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchLogs();
  }, [page, eventType, eventSubtype, actorEmail, dateFrom, dateTo]);

  const fmt = (ts) =>
    ts
      ? new Date(ts + "Z").toLocaleString("en-CA", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : "—";

  const clearFilters = () => {
    setEventType("");
    setEventSubtype("");
    setActorEmail("");
    setDateFrom("");
    setDateTo("");
    setPage(0);
  };

  const hasFilters =
    eventType || eventSubtype || actorEmail || dateFrom || dateTo;

  const totalPages = Math.ceil(total / limit);

  const inputCls =
    "px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2";
  const selectCls =
    "px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 bg-white";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">System Logs</h1>
        <p className="text-gray-500 text-sm mt-1">
          {total} event{total !== 1 ? "s" : ""} recorded
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5 space-y-3">
        <div className="flex flex-wrap gap-3">
          {/* Actor email search */}
          <div className="relative flex-1 min-w-48">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by email..."
              value={actorEmail}
              onChange={(e) => {
                setActorEmail(e.target.value);
                setPage(0);
              }}
              className={`${inputCls} w-full pl-9`}
            />
          </div>

          <select
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value);
              setEventSubtype("");
              setPage(0);
            }}
            className={selectCls}
          >
            <option value="">All Event Types</option>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace("_", " ")}
              </option>
            ))}
          </select>

          <select
            value={eventSubtype}
            onChange={(e) => {
              setEventSubtype(e.target.value);
              setPage(0);
            }}
            className={selectCls}
          >
            <option value="">All Subtypes</option>
            {Object.entries(SUBTYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 whitespace-nowrap">
              From
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(0);
              }}
              className={inputCls}
            />
            <span className="text-sm text-gray-500">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(0);
              }}
              className={inputCls}
            />
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-gray-600 underline ml-auto"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Logs table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading...</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="font-medium">No logs found</p>
          <p className="text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Event
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Actor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map((log) => {
                  const tc = EVENT_TYPE_COLORS[log.event_type] || {
                    bg: "#F3F4F6",
                    color: "#666",
                  };
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {fmt(log.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-1"
                          style={{ backgroundColor: tc.bg, color: tc.color }}
                        >
                          {log.event_type}
                        </span>
                        <p className="text-xs text-gray-600">
                          {SUBTYPE_LABELS[log.event_subtype] ||
                            log.event_subtype}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-700">
                          {log.actor_name || log.actor_email || "System"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {log.actor_role || ""}
                        </p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {log.detail && Object.keys(log.detail).length > 0 && (
                          <div className="text-xs text-gray-500 space-y-0.5">
                            {Object.entries(log.detail).map(([k, v]) => (
                              <p key={k}>
                                <span className="text-gray-400">{k}:</span>{" "}
                                {typeof v === "object"
                                  ? JSON.stringify(v)
                                  : String(v)}
                              </p>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)}{" "}
                of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm
                             disabled:opacity-40 hover:bg-gray-50 transition"
                >
                  ← Prev
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm
                             disabled:opacity-40 hover:bg-gray-50 transition"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
